/**
 * Speed limits from OpenStreetMap, per geohash-5 cell, as static assets built by
 * scripts/build-limits.mjs. The client subscribes to the same 9 cells as the
 * alert feed and map-matches the current fix to the nearest tagged segment.
 *
 * Only explicit `maxspeed` tags exist in the data: where OSM has none, Fanar
 * shows nothing rather than a guessed default.
 */
import { encodeGeohash, geohashNeighbours } from "../../shared/geohash.js";
import type { Point } from "../../shared/geo.js";

type Segment = { s: number; p: [number, number][] };
type CellFile = { cell: string; segs: Segment[] };

/** Match radius: a two-lane road plus GPS error. */
const MATCH_M = 25;
/** Heading tolerance against the segment direction (either way: roads are two-way). */
const HEADING_DEG = 50;

const R = 6371008.8;

export class SpeedLimits {
  private cells = new Map<string, Segment[]>();
  private loading = new Set<string>();
  private centre: string | null = null;
  /** Which cells exist at all (from the index), so no request ever 404s. */
  private known: Promise<Set<string> | null> | null = null;

  constructor(private readonly base = "/limits") {}

  private index(): Promise<Set<string> | null> {
    this.known ??= fetch(`${this.base}/index.json`)
      .then(async (res) => (res.ok ? new Set(((await res.json()) as { cells: string[] }).cells) : null))
      .catch(() => null);
    return this.known;
  }

  /** Keep the 9 cells around the driver loaded; fetch lazily, cache forever. */
  track(p: Point): void {
    const gh5 = encodeGeohash(p.lat, p.lon, 5);
    if (gh5 === this.centre) return;
    this.centre = gh5;
    void this.index().then((known) => {
      for (const cell of geohashNeighbours(gh5)) {
        if (this.cells.has(cell) || this.loading.has(cell)) continue;
        if (known !== null && !known.has(cell)) { this.cells.set(cell, []); continue; }
        this.loading.add(cell);
        void fetch(`${this.base}/${cell}.json`)
          .then(async (res) => { this.cells.set(cell, res.ok ? ((await res.json()) as CellFile).segs : []); })
          .catch(() => { this.cells.set(cell, []); })
          .finally(() => this.loading.delete(cell));
      }
    });
  }

  /** The limit on the road under the driver, or null when none is tagged nearby. */
  limitAt(p: Point, course: number | null): number | null {
    const gh5 = encodeGeohash(p.lat, p.lon, 5);
    const k = (Math.PI / 180) * R;
    const cosLat = Math.cos((p.lat * Math.PI) / 180);
    const px = p.lon * k * cosLat, py = p.lat * k;
    let best: { d: number; s: number } | null = null;
    for (const cell of geohashNeighbours(gh5)) {
      const segs = this.cells.get(cell);
      if (segs === undefined) continue;
      for (const seg of segs) {
        for (let i = 0; i < seg.p.length - 1; i++) {
          const a = seg.p[i] as [number, number];
          const b = seg.p[i + 1] as [number, number];
          // Cheap reject: bounding box padded by the match radius.
          if (Math.min(a[0], b[0]) - 0.0004 > p.lat || Math.max(a[0], b[0]) + 0.0004 < p.lat) continue;
          if (Math.min(a[1], b[1]) - 0.0006 > p.lon || Math.max(a[1], b[1]) + 0.0006 < p.lon) continue;
          const ax = a[1] * k * cosLat, ay = a[0] * k, bx = b[1] * k * cosLat, by = b[0] * k;
          const dx = bx - ax, dy = by - ay;
          const len2 = dx * dx + dy * dy;
          const t = len2 === 0 ? 0 : Math.max(0, Math.min(1, ((px - ax) * dx + (py - ay) * dy) / len2));
          const d = Math.hypot(px - (ax + t * dx), py - (ay + t * dy));
          if (d > MATCH_M) continue;
          if (course !== null && len2 > 0) {
            const segBearing = ((Math.atan2(dx, dy) * 180) / Math.PI + 360) % 360;
            const diff = Math.abs((((course - segBearing) % 360) + 540) % 360 - 180);
            if (Math.min(diff, 180 - diff) > HEADING_DEG) continue;
          }
          if (best === null || d < best.d) best = { d, s: seg.s };
        }
      }
    }
    return best === null ? null : best.s;
  }
}
